// POST /api/Admin/invoice-groups/backfill?corporationId={guid}&facilityId={guid}&dryRun={bool}
export interface InvoiceGroupBackfillParams {
  /** Scope to one corporation. */
  corporationId?: string;
  /** Scope to one facility. */
  facilityId?: string;
  /** true = report only. No write and no Stripe call. */
  dryRun?: boolean;
}

/** Actions applied to an invoice, comma-joined in order. Empty when nothing was applied — check `error`. */
export type InvoiceGroupBackfillOutcome =
  | 'Rerouted'
  | 'Grouped'
  | 'Rerouted,Grouped'
  | 'WouldReroute'
  | 'WouldGroup'
  | 'WouldReroute,WouldGroup';

export interface InvoiceGroupBackfillResultItem {
  invoiceId: string;
  invoiceNumber: string | null;
  facilityId: string;
  corporationId: string | null;
  /**
   * Actions applied (dry run: WouldReroute / WouldGroup variants). Empty when
   * nothing was applied for this invoice — check `error` in that case.
   */
  outcome: string;
  /** Set when this invoice could not be repaired (either batch failed). null on success. */
  error: string | null;
}

/**
 * Repair endpoint for corporate invoice billing: re-stamps invoices that were
 * left as self-pay when a facility was linked to a corporation after they were
 * generated, and files every corporate-billed-but-ungrouped invoice into its
 * group. Synchronous — it returns the full report in the response.
 *
 * Re-stamp and grouping are two separate saves. A failed save is NOT a 500: the
 * call still returns 200 with the failing invoices flagged in `results[].error`,
 * and re-running is safe (whatever did not commit is still eligible).
 */
export interface InvoiceGroupBackfillResponse {
  dryRun: boolean;
  /** Invoices found in scope. 0 = nothing to repair. */
  examined: number;
  /** Re-stamped to corporate billing. Always 0 in a dry run. */
  rerouted: number;
  /** Assigned to a group. Always 0 in a dry run. Includes this run's re-stamps. */
  grouped: number;
  /**
   * Invoices this run could not repair (either batch failed). Reported per
   * invoice in `results[].error` — never aborts the request.
   */
  failed: number;
  /** One entry per invoice touched. Empty when nothing was in scope. */
  results: InvoiceGroupBackfillResultItem[];
}
